import { alpha } from "@mui/material/styles";

function createGradient(color1: string, color2: string) {
    return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema =
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "neutral"
    | "neutralDark";

interface NeutralPaletteOptions {
    lighter: string;
    light: string;
    main: string;
    dark: string;
    darker: string;
    contrastText: string;
}
interface GradientsPaletteOptions {
    primary: string;
    info: string;
    success: string;
    warning: string;
    error: string;
}

interface ChartPaletteOptions {
    violet: string[];
    blue: string[];
    green: string[];
    yellow: string[];
    red: string[];
}

interface CustomPaletteOptions {
    iconButton: string;
}

declare module "@mui/material/styles/createPalette" {
    interface TypeBackground {
        neutral: string;
        neutralDark: string;
        neutralLight: string;
        caution: string;
        textField: string;
    }
    interface TypeText {
        inputLabel: string;
    }
    interface SimplePaletteColorOptions {
        lighter: string;
        darker: string;
    }
    interface PaletteColor {
        lighter: string;
        darker: string;
    }
    interface Palette {
        gradients: GradientsPaletteOptions;
        chart: ChartPaletteOptions;
        custom: CustomPaletteOptions;
        neutral: NeutralPaletteOptions;
        neutralDark: NeutralPaletteOptions;
    }
    interface PaletteOptions {
        gradients: GradientsPaletteOptions;
        chart: ChartPaletteOptions;
        custom: CustomPaletteOptions;
        neutral: NeutralPaletteOptions;
        neutralDark: NeutralPaletteOptions;
    }
}

declare module "@mui/material" {
    interface Color {
        0: string;
        500_8: string;
        500_12: string;
        500_16: string;
        500_24: string;
        500_32: string;
        500_48: string;
        500_56: string;
        500_80: string;
    }
}

// SETUP COLORS
const PRIMARY = {
    lighter: "#FFCCA2",
    light: "#FF8438",
    main: "#FF5004",
    dark: "#D64202",
    darker: "#732504",
};
const SECONDARY = {
    lighter: "#D6E4FF",
    light: "#84A9FF",
    main: "#3366FF",
    dark: "#1939B7",
    darker: "#091A7A",
};
const INFO = {
    lighter: "#BED2FF",
    light: "#6D97F7",
    main: "#3A68D2",
    dark: "#074397",
    darker: "#031C52",
};
const SUCCESS = {
    lighter: "#DCFEB6",
    light: "#A0EA4C",
    main: "#6BB813",
    dark: "#4C840D",
    darker: "#305506",
};
const WARNING = {
    lighter: "#FFECBD",
    light: "#FFD772",
    main: "#FEB600",
    dark: "#CC9301",
    darker: "#684B02",
};
const ERROR = {
    lighter: "#FCD4CB",
    light: "#F69179",
    main: "#FF3504",
    dark: "#CA2C05",
    darker: "#621300",
};
const NEUTRAL = {
    lighter: "#bdbdbd",
    light: "#9e9e9e",
    main: "#757575",
    dark: "#616161",
    darker: "#424242",
};

const NEUTRAL_DARK = {
    lighter: "#B3B3B3",
    light: "#787878",
    main: "#575654",
    dark: "#3A3A3A",
    darker: "#1F1F1F",
};

const GREY = {
    0: "#FFFFFF",
    100: "#F9FAFB",
    200: "#F8F4F4",
    300: "#E8DFDF",
    400: "#D3CACA",
    500: "#AC9F9F",
    600: "#847979",
    700: "#5E5252",
    800: "#373131",
    900: "#221E1E",
    500_8: alpha("#AC9F9F", 0.08),
    500_12: alpha("#AC9F9F", 0.12),
    500_16: alpha("#AC9F9F", 0.16),
    500_24: alpha("#AC9F9F", 0.24),
    500_32: alpha("#AC9F9F", 0.32),
    500_48: alpha("#AC9F9F", 0.48),
    500_56: alpha("#AC9F9F", 0.56),
    500_80: alpha("#AC9F9F", 0.8),
};

const GRADIENTS = {
    primary: createGradient(PRIMARY.light, PRIMARY.main),
    info: createGradient(INFO.light, INFO.main),
    success: createGradient(SUCCESS.light, SUCCESS.main),
    warning: createGradient(WARNING.light, WARNING.main),
    error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
    violet: ["#826AF9", "#9E86FF", "#D0AEFF", "#F7D2FF"],
    blue: ["#2D99FF", "#83CFFF", "#A5F3FF", "#CCFAFF"],
    green: ["#2CD9C5", "#60F1C8", "#A4F7CC", "#C0F2DC"],
    yellow: ["#FFE700", "#FFEF5A", "#FFF7AE", "#FFF3D6"],
    red: ["#FF6C40", "#FF8F6D", "#FFBD98", "#FFF2D4"],
};

const COMMON = {
    common: { black: "#000", white: "#fff" },
    primary: { ...PRIMARY, contrastText: "#fff" },
    secondary: { ...SECONDARY, contrastText: "#fff" },
    info: { ...INFO, contrastText: "#fff" },
    success: { ...SUCCESS, contrastText: "#fff" },
    warning: { ...WARNING, contrastText: "#fff" },
    error: { ...ERROR, contrastText: "#fff" },
    neutral: { ...NEUTRAL, contrastText: "#fff" },
    neutralDark: { ...NEUTRAL_DARK, contrastText: "#fff" },
    grey: GREY,
    gradients: GRADIENTS,
    chart: CHART_COLORS,
    divider: GREY[500_24],
    action: {
        hover: GREY[500_8],
        selected: GREY[500_16],
        disabled: GREY[500_80],
        disabledBackground: GREY[500_24],
        focus: GREY[500_24],
        hoverOpacity: 0.08,
        disabledOpacity: 0.48,
    },
    custom: {
        iconButton: "#919EAB",
    },
};

const palette = {
    light: {
        ...COMMON,
        mode: "light",
        text: {
            primary: GREY[800],
            secondary: GREY[600],
            disabled: GREY[500],
            inputLabel: "#817373",
            icon: "#757575",
        },
        background: {
            paper: "#fff",
            default: "#fff",
            neutral: GREY[200],
            neutralDark: "#F0E9EA",
            neutralLight: "#FBF9F9",
            caution: "#F4F6F8",
            textField: "#fdfdfc",
        },
        action: { active: GREY[600], ...COMMON.action },
    },
    dark: {
        ...COMMON,
        mode: "dark",
        text: {
            primary: "#fff",
            secondary: GREY[500],
            disabled: GREY[600],
            inputLabel: "#817373",
        },
        background: {
            paper: GREY[800],
            default: GREY[900],
            neutral: GREY[500_16],
            neutralDark: "#F0E9EA",
            neutralLight: "#FBF9F9",
            caution: "#F4F6F8",
            textField: "#fdfdfc",
        },
        action: { active: GREY[500], ...COMMON.action },
    },
} as const;

export default palette;
